import {
  EnsureInfiniteQueryDataOptions,
  GetNextPageParamFunction,
  InfiniteData,
  QueryClient,
} from "@tanstack/react-query";
import type {
  InfiniteQueryMethodForModel,
  ModelName,
  ParamsForMethod,
  ReturnForMethod,
  TellescopeQueryKey,
  SupportedSession,
} from "../../types";
import { buildTellescopeQueryKey } from "../../keys";
import { executeTellescopeApiMethod } from "../execute-tellescope-api-method";

type EnsureTellescopeInfiniteQueryDataOptions<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = InfiniteData<ReturnForMethod<Model, Method>, TPageParam>,
> = Omit<
  EnsureInfiniteQueryDataOptions<
    ReturnForMethod<Model, Method>,
    TError,
    TData,
    TellescopeQueryKey<Model, Method>,
    TPageParam
  >,
  "queryKey" | "queryFn" | "getPreviousPageParam"
>;

/**
 * Arguments for ensuring infinite query data exists in cache.
 *
 * @template Model - The Tellescope model name (infinite query models only)
 * @template Method - The query method for the model (infinite query methods only)
 * @template TPageParam - The page parameter type
 * @template TError - The error type (defaults to Error)
 * @template TData - The transformed data type (defaults to InfiniteData<method's return type>)
 */
export interface EnsureTellescopeInfiniteQueryDataArgs<
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = InfiniteData<ReturnForMethod<Model, Method>, TPageParam>,
> extends EnsureTellescopeInfiniteQueryDataOptions<Model, Method, TPageParam, TError, TData> {
  /** The Tellescope model name */
  model: Model;
  /** The query method to call on the model */
  method: Method;
  /** The base request parameters (will be merged with page params in queryFn) */
  request: ParamsForMethod<Model, Method>;
  /** The TanStack Query client instance */
  queryClient: QueryClient;
  /** The Tellescope session instance */
  session: SupportedSession;
  /** Optional function to merge page param into request */
  paginateRequest?: (pageParam: TPageParam, request: ParamsForMethod<Model, Method>) => ParamsForMethod<Model, Method>;
  /** The initial page parameter value */
  initialPageParam: TPageParam;
  /** Function to get the next page parameter from the last page */
  getNextPageParam: GetNextPageParamFunction<TPageParam, ReturnForMethod<Model, Method>>;
  requestKeysToOmitFromQueryKey?: (keyof ParamsForMethod<Model, Method>)[];
}

/**
 * Ensures infinite query data exists in the cache, fetching it if necessary.
 * This is useful when you need to guarantee that paginated data exists before proceeding.
 * Returns cached data if fresh, otherwise fetches new data.
 *
 * @param args - The ensure infinite query data arguments
 * @returns A promise that resolves to the infinite query data (or transformed data if select is used)
 *
 * @example
 * ```tsx
 * const users = await ensureTellescopeInfiniteQueryData({
 *   queryClient,
 *   session,
 *   model: "users",
 *   method: "getSome",
 *   request: { filter: {}, limit: 10 },
 *   initialPageParam: undefined,
 *   getNextPageParam: (lastPage) => {
 *     if (!Array.isArray(lastPage) || lastPage.length === 0) return null;
 *     return lastPage[lastPage.length - 1].id;
 *   },
 * }); // Returns InfiniteData<User[]>
 * ```
 */
export const ensureTellescopeInfiniteQueryData = <
  Model extends ModelName,
  Method extends InfiniteQueryMethodForModel<Model>,
  TPageParam,
  TError = Error,
  TData = ReturnForMethod<Model, Method>,
>({
  model,
  method,
  request,
  queryClient,
  session,
  paginateRequest,
  initialPageParam,
  getNextPageParam,
  requestKeysToOmitFromQueryKey,
  ...tanstackOptions
}: EnsureTellescopeInfiniteQueryDataArgs<Model, Method, TPageParam, TError, TData>) => {
  const queryKey = buildTellescopeQueryKey({ model, method, request, requestKeysToOmitFromQueryKey });

  return queryClient.ensureInfiniteQueryData({
    ...tanstackOptions,
    queryKey,
    initialPageParam,
    getNextPageParam,
    queryFn: ({ pageParam }) =>
      executeTellescopeApiMethod<Model, Method, false>({
        session,
        model,
        method,
        // @ts-expect-error - TypeScript doesn't infer pageParam type from generic
        request: paginateRequest ? paginateRequest(pageParam, request) : request,
        parseISOStrings: true, // TODO: improve this by making it an option locally and globally
      }),
  });
};
